import React from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import MicNoneIcon from "@mui/icons-material/MicNone";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PersonIcon from "@mui/icons-material/Person";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import useMatchPhoto from "./useMatchPhoto";
import MatchmakerLogo from "../../../components/MatchmakerLogo";

const dotColor = (c) => {
  if (c >= 0.75) return "#2e9b5b";
  if (c >= 0.5) return "#e0952f";
  return "#b0b0b0";
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

const PREVIEW_LIMIT = 6;

const TraitChip = ({ trait, tint }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 0.6,
      borderRadius: 5,
      bgcolor: tint,
      fontSize: 13,
      lineHeight: 1.2,
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: dotColor(trait.confidence),
        flexShrink: 0,
      }}
    />
    {trait.value}
  </Box>
);

const StatTile = ({ value, label }) => (
  <Box sx={{ flex: 1, textAlign: "center", py: 1.5 }}>
    <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
      {label}
    </Typography>
  </Box>
);

const Section = ({ title, count, tint, traits, onReviewAll }) => (
  <Box sx={{ mt: 3 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        mb: 1.25,
      }}
    >
      <Typography sx={{ fontSize: 17, fontWeight: 600 }}>{title}</Typography>
      {count > 0 ? (
        <Box
          onClick={onReviewAll}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 13,
            fontWeight: 600,
            color: "primary.main",
            cursor: "pointer",
          }}
        >
          <EditOutlinedIcon sx={{ fontSize: 15 }} />
          {`Review & edit ${count}`}
        </Box>
      ) : null}
    </Box>
    {count > 0 ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {traits.slice(0, PREVIEW_LIMIT).map((t) => (
          <TraitChip key={t.id} trait={t} tint={tint} />
        ))}
      </Box>
    ) : (
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        Nothing here yet. Record a note to fill it in.
      </Typography>
    )}
  </Box>
);

const StackAvatar = ({ match, index }) => {
  const photo = useMatchPhoto(match);
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        bgcolor: "#d7b7a7",
        border: "2px solid #fff",
        ml: index === 0 ? 0 : -1.25,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        overflow: "hidden",
        backgroundImage: photo ? `url(${photo.url})` : "none",
        backgroundSize: "cover",
        backgroundPosition: photo ? `${photo.focalX}% ${photo.focalY}%` : "center",
      }}
    >
      {photo ? null : (
        <PersonIcon sx={{ fontSize: 28, color: "rgba(0,0,0,0.3)" }} />
      )}
    </Box>
  );
};

const AvatarStack = ({ matches }) => {
  const shown = (matches || []).slice(0, 3);
  return (
    <Box sx={{ display: "flex", flexShrink: 0 }}>
      {shown.map((m, i) => (
        <StackAvatar key={m.id} match={m} index={i} />
      ))}
    </Box>
  );
};

const MatchesCard = ({ matches, onViewMatches }) => {
  const active = matches || [];
  const total = active.length;
  const newMatches = active.filter((m) => !m.connected);
  const mutualMatches = active.filter((m) => m.connected);
  const newCount = newMatches.length;
  const mutualCount = mutualMatches.length;

  if (total === 0) {
    return (
      <Box
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "rgba(239,82,65,0.25)",
          display: "flex",
          gap: 1.25,
        }}
      >
        <FavoriteBorderIcon sx={{ color: "primary.main", flexShrink: 0 }} />
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            Matches are on the way
          </Typography>
          <Typography
            sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5, mt: 0.25 }}
          >
            When we find someone who could be a great match, we'll send you a
            notification. Come back here to view them.
          </Typography>
        </Box>
      </Box>
    );
  }

  const headline =
    newCount > 0
      ? `${newCount} new ${newCount === 1 ? "match" : "matches"}`
      : mutualCount > 0
        ? `${mutualCount} mutual ${mutualCount === 1 ? "match" : "matches"}`
        : "Your matches";
  const sub = newCount > 0 ? "Tap to review and respond" : "Tap to view";
  const avatarMatches = newCount > 0 ? newMatches : mutualMatches;
  const showMutualPill = newCount > 0 && mutualCount > 0;

  return (
    <Box
      onClick={onViewMatches}
      sx={{
        mt: 2.5,
        p: 2,
        borderRadius: 3,
        bgcolor: "rgba(46,155,91,0.12)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        cursor: "pointer",
      }}
    >
      <AvatarStack matches={avatarMatches} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{headline}</Typography>
          {showMutualPill ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                px: 0.9,
                py: 0.25,
                borderRadius: 5,
                bgcolor: "#fff",
                color: "#2e9b5b",
                border: "1px solid rgba(46,155,91,0.4)",
              }}
            >
              <FavoriteIcon sx={{ fontSize: 11 }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                {`${mutualCount} mutual`}
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{sub}</Typography>
      </Box>
      <ChevronRightIcon sx={{ color: "#2e9b5b", flexShrink: 0 }} />
    </Box>
  );
};

const ProfileHome = ({
  profile,
  traits,
  matches,
  onAddNote,
  onReviewAll,
  onViewMatches,
}) => {
  const navigate = useNavigate();
  const selfCount = traits.self.length;
  const partnerCount = traits.partner.length;
  const date = formatDate(profile && profile.lastUpdated);
  const noteCount = (profile && profile.noteCount) || 0;

  const subtitleParts = [];
  if (date) subtitleParts.push(`Last updated ${date}.`);
  if (noteCount > 0) {
    subtitleParts.push(`Built from ${noteCount} ${noteCount === 1 ? "note" : "notes"}.`);
  }

  return (
    <Box sx={{ p: 2.5, pb: 12, maxWidth: 560, mx: "auto" }}>
      <Typography
        onClick={() => navigate("/profile")}
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "primary.main",
          cursor: "pointer",
          mb: 2,
        }}
      >
        {"‹ Profile"}
      </Typography>

      <Box sx={{ textAlign: "center", mb: 2.5 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            bgcolor: "rgba(239,82,65,0.12)",
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 1.5,
          }}
        >
          <MatchmakerLogo size={40} />
        </Box>
        <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
          Your Matchmaker is live
        </Typography>
        {subtitleParts.length ? (
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
            {subtitleParts.join(" ")}
          </Typography>
        ) : null}
        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1 }}>
          Review each section and remove anything that doesn't fit.
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{ display: "flex", borderRadius: 3, overflow: "hidden" }}
      >
        <StatTile value={selfCount} label="About you" />
        <Box sx={{ width: "1px", bgcolor: "divider" }} />
        <StatTile value={partnerCount} label="Looking for" />
      </Paper>

      <MatchesCard matches={matches} onViewMatches={onViewMatches} />

      <Box
        sx={{
          mt: 2.5,
          p: 2,
          borderRadius: 3,
          bgcolor: "rgba(239,82,65,0.08)",
        }}
      >
        <Box sx={{ display: "flex", gap: 1.25 }}>
          <MicNoneIcon sx={{ color: "primary.main", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.5 }}>
            Anything new to add? Record another note and we'll pull out what's
            new and add it to your profile.
          </Typography>
        </Box>
        <Typography
          onClick={onAddNote}
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: "primary.main",
            cursor: "pointer",
            mt: 1.25,
          }}
        >
          + Record a new note
        </Typography>
      </Box>

      <Section
        title="About you"
        count={selfCount}
        tint="rgba(46,155,91,0.10)"
        traits={traits.self}
        onReviewAll={() => onReviewAll("self")}
      />
      <Section
        title="Looking for"
        count={partnerCount}
        tint="rgba(239,82,65,0.08)"
        traits={traits.partner}
        onReviewAll={() => onReviewAll("partner")}
      />

      <Box sx={{ display: "flex", gap: 1.5, mt: 4 }}>
        <Button
          variant="outlined"
          fullWidth
          startIcon={<EditOutlinedIcon />}
          onClick={() => onReviewAll("self")}
        >
          Review & edit
        </Button>
        <Button variant="contained" fullWidth onClick={onAddNote}>
          New note
        </Button>
      </Box>
    </Box>
  );
};

export default ProfileHome;
